import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./CustomScrollbar.module.css";

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function CustomScrollbar() {
  const trackRef = useRef(null);
  const activityTimeoutRef = useRef(null);
  const dragStateRef = useRef({
    dragging: false,
    startY: 0,
    startScrollTop: 0,
  });
  const [isActive, setIsActive] = useState(false);
  const [metrics, setMetrics] = useState({
    viewportHeight: 0,
    scrollHeight: 0,
    scrollTop: 0,
  });

  const showTemporarily = () => {
    window.clearTimeout(activityTimeoutRef.current);
    setIsActive(true);
    activityTimeoutRef.current = window.setTimeout(() => {
      if (!dragStateRef.current.dragging) {
        setIsActive(false);
      }
    }, 900);
  };

  useEffect(() => {
    const updateMetrics = () => {
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      setMetrics({
        viewportHeight: clientHeight,
        scrollHeight,
        scrollTop,
      });
      showTemporarily();
    };

    updateMetrics();
    window.addEventListener("scroll", updateMetrics, { passive: true });
    window.addEventListener("resize", updateMetrics);

    return () => {
      window.clearTimeout(activityTimeoutRef.current);
      window.removeEventListener("scroll", updateMetrics);
      window.removeEventListener("resize", updateMetrics);
    };
  }, []);

  useEffect(() => {
    const handlePointerMove = (event) => {
      if (!dragStateRef.current.dragging || !trackRef.current) return;

      const trackHeight = trackRef.current.clientHeight;
      const maxScroll = Math.max(
        metrics.scrollHeight - metrics.viewportHeight,
        0
      );

      if (trackHeight <= 0 || maxScroll <= 0) return;

      const thumbHeight = Math.max(
        (metrics.viewportHeight / metrics.scrollHeight) * trackHeight,
        56
      );
      const availableTrack = Math.max(trackHeight - thumbHeight, 1);
      const deltaY = event.clientY - dragStateRef.current.startY;
      const scrollDelta = (deltaY / availableTrack) * maxScroll;

      window.scrollTo({
        top: clamp(
          dragStateRef.current.startScrollTop + scrollDelta,
          0,
          maxScroll
        ),
      });
    };

    const handlePointerUp = () => {
      dragStateRef.current.dragging = false;
      document.body.classList.remove("custom-scrollbar-dragging");
      showTemporarily();
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [metrics.scrollHeight, metrics.viewportHeight]);

  const { thumbHeight, thumbOffset, isScrollable } = useMemo(() => {
    const trackHeight = metrics.viewportHeight;
    const maxScroll = Math.max(metrics.scrollHeight - metrics.viewportHeight, 0);
    const scrollable = maxScroll > 0 && trackHeight > 0;

    if (!scrollable) {
      return { thumbHeight: 0, thumbOffset: 0, isScrollable: false };
    }

    const nextThumbHeight = Math.max(
      (metrics.viewportHeight / metrics.scrollHeight) * trackHeight,
      56
    );
    const availableTrack = Math.max(trackHeight - nextThumbHeight, 1);
    const nextThumbOffset = (metrics.scrollTop / maxScroll) * availableTrack;

    return {
      thumbHeight: nextThumbHeight,
      thumbOffset: nextThumbOffset,
      isScrollable: true,
    };
  }, [metrics]);

  const handleTrackClick = (event) => {
    if (!trackRef.current || event.target !== trackRef.current || !isScrollable) {
      return;
    }

    const rect = trackRef.current.getBoundingClientRect();
    const clickOffset = event.clientY - rect.top - thumbHeight / 2;
    const availableTrack = Math.max(rect.height - thumbHeight, 1);
    const maxScroll = Math.max(metrics.scrollHeight - metrics.viewportHeight, 0);
    const nextScrollTop = (clamp(clickOffset, 0, availableTrack) / availableTrack) * maxScroll;

    window.scrollTo({ top: nextScrollTop, behavior: "smooth" });
  };

  const handleThumbPointerDown = (event) => {
    dragStateRef.current = {
      dragging: true,
      startY: event.clientY,
      startScrollTop: metrics.scrollTop,
    };
    document.body.classList.add("custom-scrollbar-dragging");
    window.clearTimeout(activityTimeoutRef.current);
    setIsActive(true);
  };

  if (!isScrollable) return null;

  return (
    <div
      ref={trackRef}
      className={`${styles.scrollbar} ${isActive ? styles.active : ""}`}
      onClick={handleTrackClick}
      onMouseEnter={() => {
        window.clearTimeout(activityTimeoutRef.current);
        setIsActive(true);
      }}
      onMouseLeave={() => {
        if (!dragStateRef.current.dragging) {
          showTemporarily();
        }
      }}
      aria-hidden="true"
    >
      <div
        className={styles.thumb}
        style={{
          height: `${thumbHeight}px`,
          transform: `translateY(${thumbOffset}px)`,
        }}
        onPointerDown={handleThumbPointerDown}
      />
    </div>
  );
}

export default CustomScrollbar;
